import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface Settings {
  id?: string;           // optional, depending on your backend model
  themeColor: string;
  language: string;
  notifications: boolean;
  logoUrl: string;
  isDarkMode?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.idToken;
    if (!token) {
      throw new Error('User not authenticated');
    }
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Fetch settings for current user
   * GET /api/settings
   */
  getSettings(): Observable<Settings> {
    return this.http.get<Settings>(
      `${this.baseUrl}/settings`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Create settings for current user
   * POST /api/settings
   */
  createSettings(settings: Settings): Observable<Settings> {
    return this.http.post<Settings>(
      `${this.baseUrl}/settings`,
      settings,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Update settings for current user
   * PUT /api/settings
   */
  updateSettings(settings: Partial<Settings>): Observable<Settings> {
    return this.http.put<Settings>(
      `${this.baseUrl}/settings`,
      settings,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Delete settings for current user
   * DELETE /api/settings
   */
  deleteSettings(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.baseUrl}/settings`,
      { headers: this.getAuthHeaders() }
    );
  }
}